/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Box,
  FormControl,
  IconButton,
  FormHelperText as MuiFormHelperText,
  InputAdornment,
  Typography,
  Link,
  styled,
} from '@mui/material';
import {useFormik} from 'formik';
import Button from '@jobseeker/lotus/elements/ButtonV2';
import {
  useAlertCallback,
  useValidateComponent,
} from '@jobseeker/lotus/hooks';
import {VersionVariants} from '@jobseeker/lotus/enums';
import {useState} from 'react';
import Input from '@jobseeker/lotus/elements/Input';
import {Image} from '@jobseeker/lotus/components/Image';
import {Eye, HiddenEye} from '@jobseeker/lotus/assets/icons';
import React from 'react';
import * as yup from 'yup';
import {Tooltip} from '@jobseeker/lotus/elements';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import UserService from '@jobseeker/common-core/services/user.service';
import {enqueueSnackbar} from 'notistack';
import authService from '@jobseeker/common-core/services/auth.service';
import {useSelector} from 'react-redux';
import {AppState} from '@jobseeker/common-core/types';
import {TrackEventUBA} from '@jobseeker/common-core/utils';

const validationSchemaChangePassword = yup.object({
  password: yup
    .string()
    .min(8, 'Password must be 8 characters or more')
    .required('Please enter your password'),
  oldpassword: yup
    .string()
    .min(8, 'Password must be 8 characters or more')
    .required('Please enter your password'),
  confirmpassword: yup
    .string()
    .required('Please enter your password')
    .oneOf(
      [yup.ref('password')],
      'New password and confirmation password do not match'
    ),
});

const FormHelperText = styled(MuiFormHelperText)(() => ({
  position: 'absolute',
}));

const ChangePasswordForm = ({onSuccess, onError, onCancel, ...props}: any) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [open, setOpen] = React.useState(false);
  const [handlePasswordRegexValidation, PasswordValidationComponent] =
    useValidateComponent();

  const handleTooltipClose = () => {
    setOpen(false);
  };

  const handleTooltipOpen = () => {
    setOpen(true);
  };
  const formik = useFormik({
    initialValues: {
      password: '',
      confirmpassword: '',
      oldpassword: '',
    },
    validationSchema: validationSchemaChangePassword,
    onSubmit: async (values: any) => {
      // console.log(values);
      const resp = await UserService.changePassword(values);
      if (resp?.error) {
        formik.setFieldError(
          resp.error.name === 'USER_PASSWORD_MISMATCHED'
            ? 'oldpassword'
            : 'password',
          resp.error.message
        );
        onError();
      } else {
        enqueueSnackbar(
          'Your password has been updated successfully. For your security, you have been logged out from all devices. Please log in again using your new password.',
          {
            variant: 'success',
          }
        );
        onSuccess();
      }
    },
    validateOnChange: true,
    validateOnBlur: true,
    validate: async values => {
      handlePasswordRegexValidation(values.password);
    },
  });
  const profile = useSelector((state: AppState) => state.user.profile);

  const onForgotPassword = async () => {
    const resInfo = await authService.forgotPassword('forgot', {
      email: profile.email,
    });
    onCancel();
    props.onForgotPassword();
  };

  const isInputEmpty =
    formik?.values?.password.trim() === '' ||
    formik?.values?.confirmpassword.trim() === '' ||
    formik?.values?.oldpassword.trim() === '';
  const hasError =
    (formik.touched.oldpassword && Boolean(formik.errors.oldpassword)) ||
    (formik.touched.confirmpassword &&
      Boolean(formik.errors.confirmpassword)) ||
    (formik.touched.password && Boolean(formik.errors.password));

  return (
    <Box
      component={'form'}
      onSubmit={e => {
        e.preventDefault();
        formik.handleSubmit();
      }}
    >
      <FormControl
        sx={{width: '100%', mt: 1.5}}
        error={formik.touched.oldpassword && Boolean(formik.errors.oldpassword)}
      >
        <Typography variant="subtitle3">Original Password</Typography>
        <Input
          variant={VersionVariants.VERSION_THREE}
          type={showPassword ? 'text' : 'Password'}
          name="oldpassword"
          value={formik.values.oldpassword}
          onChange={formik.handleChange}
          error={
            formik.touched.oldpassword && Boolean(formik.errors.oldpassword)
          }
          placeholder="Original Password"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <Image
                      src={Eye}
                      width={15}
                      height={15}
                      alt="show password"
                    />
                  ) : (
                    <Image
                      src={HiddenEye}
                      width={15}
                      height={15}
                      alt="hide password"
                    />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Box display={'flex'} justifyContent={'space-between'}>
          <FormHelperText
            sx={{
              opacity: Boolean(
                formik.touched.oldpassword && formik.errors.oldpassword
              )
                ? 1
                : 0,
              width: {xxs: 'calc(100% - 150px)', md: '100%'},
            }}
          >
            <>{formik?.errors?.oldpassword || ''}</>
          </FormHelperText>
          <Typography
            variant="body3"
            pr={2}
            sx={{
              minWidth: '100%',
              textAlign: 'right',
            }}
          >
            <Link
              sx={{textDecoration: 'none', cursor: 'pointer'}}
              onClick={onForgotPassword}
            >
              Forgot Password?
            </Link>
          </Typography>
        </Box>
      </FormControl>

      <FormControl
        sx={{width: '100%', mt: 1.25}}
        error={formik.touched.password && Boolean(formik.errors.password)}
      >
        <Typography variant="subtitle3">New Password</Typography>
        <ClickAwayListener onClickAway={handleTooltipClose}>
          <div>
            <Tooltip
              PopperProps={{
                disablePortal: true,
              }}
              onClose={handleTooltipClose}
              open={open}
              disableFocusListener
              disableHoverListener
              disableTouchListener
              title={<PasswordValidationComponent />}
              arrow
              placement="bottom-start"
            >
              <Box onClick={handleTooltipOpen}>
                <Input
                  variant={VersionVariants.VERSION_THREE}
                  type={showNewPassword ? 'text' : 'Password'}
                  name="password"
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.password && Boolean(formik.errors.password)
                  }
                  placeholder="New Password"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          // onMouseDown={handleMouseDown}
                          // edge="end"
                        >
                          {showNewPassword ? (
                            <Image
                              src={Eye}
                              width={15}
                              height={15}
                              alt="show password"
                            />
                          ) : (
                            <Image
                              src={HiddenEye}
                              width={15}
                              height={15}
                              alt="hide password"
                            />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            </Tooltip>
          </div>
        </ClickAwayListener>

        {formik.touched.password && Boolean(formik.errors.password) && (
          <Box display={'flex'}>
            <FormHelperText>
              <>{formik?.errors?.password || ''}</>
            </FormHelperText>
          </Box>
        )}
      </FormControl>

      <FormControl
        sx={{width: '100%', mt: 3.5, position: 'relative'}}
        error={
          formik.touched.confirmpassword &&
          Boolean(formik.errors.confirmpassword)
        }
      >
        <Typography variant="subtitle3">Confirm New Password</Typography>
        <Input
          variant={VersionVariants.VERSION_THREE}
          type={showConfirmPassword ? 'text' : 'Password'}
          name="confirmpassword"
          value={formik.values.confirmpassword}
          onChange={formik.handleChange}
          error={
            formik.touched.confirmpassword &&
            Boolean(formik.errors.confirmpassword)
          }
          placeholder="Confirm New Password"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  // onMouseDown={handleMouseDown}
                  // edge="end"
                >
                  {showConfirmPassword ? (
                    <Image
                      src={Eye}
                      width={15}
                      height={15}
                      alt="show password"
                    />
                  ) : (
                    <Image
                      src={HiddenEye}
                      width={15}
                      height={15}
                      alt="hide password"
                    />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        {formik?.touched?.confirmpassword &&
          Boolean(formik?.errors?.confirmpassword) && (
            <Box display={'flex'}>
              <FormHelperText>
                <>{formik?.errors?.confirmpassword || ''}</>
              </FormHelperText>
            </Box>
          )}
      </FormControl>

      <Box display={'flex'} gap={1.5} mt={4} mb={2}>
        <Button
          color="secondary"
          variant="contained"
          disabled={hasError || isInputEmpty}
          type="submit"
        >
          Confirm
        </Button>
        <Button onClick={onCancel} color="primary" variant="outlined">
          Cancel
        </Button>
      </Box>
    </Box>
  );
};
export const PasswordComponent: React.FC<object> = ({}) => {
  const profile = useSelector((state: AppState) => state.user.profile);

  const [ForgotPasswordDialog, toggleForgotPassword] = useAlertCallback({
    title: (
      <Typography
        component={'span'}
        fontSize={'inherit'}
        fontWeight={'inherit'}
      >
        We’ve sent an email to {profile.email || 'your email'}
        <br /> Click the link in the email to rest your password.
      </Typography>
    ),
    subtitle:
      'If you don’t see the email, check other places it might be, like your junk, spam, social, or other folders.',
    primaryAction: 'Okay',
    // secondaryAction: 'Cancel',
    primaryCallback: console.log,
    useBottomSheet: true,
  });

  const [ChangePasswordDialog, toggleChangePassword] = useAlertCallback({
    title: 'Change Password',
    subtitle: 'Fill in the details to change your password',
    BodyContent: () => (
      <ChangePasswordForm
        onSuccess={toggleChangePassword}
        onCancel={toggleChangePassword}
        onError={console.log}
        onForgotPassword={toggleForgotPassword}
      />
    ),
    isPrimaryActionDisabled: true,
    // primaryAction: 'Confirm',
    // secondaryAction: 'Cancel',
    primaryCallback: console.log,
    hideActions: true,
    useBottomSheet: true,
  });

  const handleChangePasswordClick = () => {
    const url = new URL(window.location.href);
    const ref = url.searchParams.get('ref');
    TrackEventUBA('changePasswordPageView', 'view', {
      ref: ref || '',
      pageName: 'Change Password Page',
      sessionUsername: sessionStorage.getItem('userId') || '',
    });

    toggleChangePassword();
  };
  return (
    <>
      <ForgotPasswordDialog />
      <Box
        color="gray.dark"
        onClick={() => {}}
        data-testid="notification_list_item"
        sx={{
          position: 'relative',
          transition: 'all ease 0.2s',
          borderBottom: 'solid 1px',
          borderColor: 'border.lightGray',
        }}
      >
        <Box
          sx={{
            py: {xxs: 1.5, md: 2},
            px: {xxs: 1.5, md: 2},
          }}
        >
          <Box
            display={'flex'}
            width={'100%'}
            justifyContent={'space-between'}
            alignItems={'center'}
          >
            <Box display={'flex'} flexDirection={'column'}>
              <Typography
                variant="subtitle2"
                color={'text.primary'}
                sx={{
                  lineClamp: '3',
                  '-webkit-line-clamp': '2',
                  '-webkit-box-orient': 'vertical',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  wordWrap: 'break-word',
                  display: 'block',
                  alignItems: 'center',
                }}
              >
                Password:&nbsp;***********
              </Typography>
              <Button
                variant="text"
                onClick={handleChangePasswordClick}
                sx={{
                  padding: '0px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'var(--brand-color, #49966E)',
                  display: 'flex',
                  justifyContent: 'flex-start',
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                }}
              >
                Change Password
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
      <ChangePasswordDialog />
    </>
  );
};
export default PasswordComponent;
